/** RoyalEngagement — Hero. Full-bleed with animated rose petals and ring box. */

import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CalendarDays, MapPin, ChevronDown } from 'lucide-react';
import { ROYAL_EASE } from '../theme';
import type { RoyalContent } from '../content';

// Rose petals component
function RosePetals() {
  const petals = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    size: 12 + Math.random() * 16,
  }));

  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {petals.map((petal) => (
        <motion.div
          key={petal.id}
          className="absolute"
          style={{ left: petal.left, top: -30 }}
          initial={{ y: -30, rotate: 0, opacity: 0 }}
          animate={{
            y: ['100vh', '110vh'],
            rotate: [0, 360, 720],
            opacity: [0, 1, 1, 0],
            x: [0, 20, -20, 10, 0],
          }}
          transition={{
            duration: petal.duration,
            delay: petal.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <svg
            width={petal.size}
            height={petal.size}
            viewBox="0 0 24 24"
            fill="#c9a227"
            opacity="0.6"
          >
            <path d="M12 2C12 2 8 6 8 12C8 18 12 22 12 22C12 22 16 18 16 12C16 6 12 2 12 2Z" />
            <path d="M12 2C12 2 6 6 6 12C6 18 12 22 12 22C12 22 18 18 18 12C18 6 12 2 12 2Z" fill="#e8c547" opacity="0.5" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

// Ring box animation component
function RingBox() {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <motion.div
      className="absolute right-8 bottom-12 hidden lg:flex flex-col items-center cursor-pointer"
      onClick={() => setIsOpen(!isOpen)}
      whileHover={{ scale: 1.05 }}
    >
      <motion.div
        className="relative w-24 h-20 bg-gradient-to-br from-[#c9a227] to-[#8b7355] rounded-lg shadow-2xl"
        animate={isOpen ? { rotateX: -30 } : { rotateX: 0 }}
        transition={{ duration: 0.6, ease: ROYAL_EASE }}
      >
        {/* Box lid */}
        <motion.div
          className="absolute -top-3 left-0 w-full h-6 bg-gradient-to-r from-[#e8c547] to-[#c9a227] rounded-t-lg origin-bottom"
          animate={isOpen ? { rotateX: -110 } : { rotateX: 0 }}
          transition={{ duration: 0.6, ease: ROYAL_EASE }}
        >
          <div className="absolute inset-x-0 top-1 h-1 bg-[#f0e0a0] opacity-50" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-4 h-4 rounded-full border-2 border-[#720e1e]" />
        </div>
        </motion.div>
        {/* Ring */}
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          animate={isOpen ? { y: -40, scale: 1.2 } : { y: 0, scale: 0.8 }}
          transition={{ duration: 0.8, delay: 0.2, ease: ROYAL_EASE }}
        >
          <div className="w-8 h-8 rounded-full border-4 border-[#c9a227] border-t-[#e8c547] shadow-lg" />
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          >
            <div className="w-2 h-2 rounded-full bg-white opacity-80 absolute top-0 left-1/2 -translate-x-1/2" />
          </motion.div>
        </motion.div>
      </motion.div>
      <motion.p
        className="mt-2 text-xs text-white/70 font-medium tracking-wide"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Click to reveal ✨
      </motion.p>
    </motion.div>
  );
}

export function HeroSection({
  content,
  onRsvp,
}: {
  content: RoyalContent['hero'];
  onRsvp: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div ref={ref} className="relative">
      {/* Animated rose petals */}
      <RosePetals />

      <section className="relative isolate flex min-h-[92svh] items-end overflow-hidden sm:min-h-screen">
        {/* Parallax background */}
        <motion.div
          className="absolute inset-0 -z-20"
          style={{ y: imageY, scale: imageScale }}
        >
          <img
            src={content.background}
            alt=""
            className="h-full w-full object-cover"
          />
        </motion.div>

        {/* Maroon gradient overlay */}
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,rgba(114,14,30,0.65)_0%,rgba(74,9,20,0.45)_40%,rgba(31,10,14,0.88)_100%)]" />
        {/* Gold accent glow */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_40%_at_72%_18%,rgba(201,162,39,0.35),transparent_70%)]" />

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative mx-auto w-full max-w-6xl px-5 pb-20 pt-32 sm:px-8 sm:pb-28 lg:px-12"
        >
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: ROYAL_EASE }}
            className="mb-4 text-[0.72rem] font-semibold uppercase tracking-[0.34em] text-[var(--royal-gold-soft)]"
          >
            {content.kicker}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: ROYAL_EASE }}
            className="max-w-4xl font-serif text-[clamp(2.8rem,9vw,6.8rem)] font-semibold leading-[0.95] text-white"
          >
            <span className="bg-gradient-to-r from-[#f0e0a0] via-[#c9a227] to-[#f0e0a0] bg-clip-text text-transparent">
              {content.title}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: ROYAL_EASE }}
            className="mt-3 font-serif text-xl italic text-white/80 sm:text-2xl"
          >
            {content.subtitle}
          </motion.p>

          {/* Detail chips */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: ROYAL_EASE }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#c9a227]/30 bg-[#720e1e]/30 px-4 py-2 text-sm text-white/90 backdrop-blur-md">
              <CalendarDays size={16} className="text-[#c9a227]" />
              {content.dateLabel}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#c9a227]/30 bg-[#720e1e]/30 px-4 py-2 text-sm text-white/90 backdrop-blur-md">
              <MapPin size={16} className="text-[#c9a227]" />
              {content.venueShort}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: ROYAL_EASE }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <button
              type="button"
              onClick={onRsvp}
              className="rounded-full bg-[#c9a227] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-[#1f0a0e] shadow-lg shadow-black/30 transition hover:-translate-y-0.5 hover:bg-[#f0e0a0]"
            >
              RSVP Now
            </button>
            <span className="text-sm text-white/65">
              Hosted by {content.host}
            </span>
          </motion.div>
        </motion.div>

        {/* Interactive ring box */}
        <RingBox />

        {/* Scroll cue */}
        <motion.div
          aria-hidden
          animate={{ y: [0, 9, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70"
        >
          <span className="text-[0.62rem] uppercase tracking-[0.3em]">
            Scroll
          </span>
          <ChevronDown size={18} />
        </motion.div>
      </section>
    </div>
  );
}
